// frontend/src/pages/company/Users.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// ============================================
// ШИНЭ ROLE СИСТЕМ - 3 төрөл
// ============================================
const ROLE_OPTIONS = [
  { value: 'company_admin', label: 'Админ', color: 'bg-red-100 text-red-700', icon: '👑', description: 'Бүх эрхтэй' },
  { value: 'company_manager', label: 'Менежер', color: 'bg-blue-100 text-blue-700', icon: '👔', description: 'Эрх тохируулна' },
  { value: 'company_worker', label: 'Ажилтан', color: 'bg-green-100 text-green-700', icon: '👷', description: 'Эрх тохируулна' }
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Идэвхтэй' },
  { value: 'inactive', label: 'Идэвхгүй' }
];

const Users = () => {
  const { user: currentUser, hasPermission } = useAuth();
  
  // States
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    phone: '',
    role: 'company_worker',
    status: 'active'
  });
  
  // Permission form state
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  // Password form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/company/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (err) {
      setError('Хэрэглэгчдийн мэдээлэл авахад алдаа гарлаа');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch permissions list
  const fetchPermissions = async () => {
    try {
      const response = await api.get('/company/users/permissions');
      if (response.data.success) {
        setPermissions(response.data.data);
        setGroupedPermissions(response.data.grouped);
      }
    } catch (err) {
      console.error('Permissions fetch error:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, []);

  // Auto hide messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Filter users by search
  const filteredUsers = users.filter(user => 
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      phone: '',
      role: 'company_worker',
      status: 'active'
    });
    setShowModal(true);
  };

  // Open edit modal
  const openEditModal = (user) => {
    if (user.is_primary) {
      setError('Үндсэн админыг засах боломжгүй');
      return;
    }
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name,
      email: user.email || '',
      phone: user.phone || '',
      role: user.role,
      status: user.status
    });
    setShowModal(true);
  };

  // Open detail modal
  const openDetailModal = async (user) => {
    setSelectedUser(user);
    try {
      const response = await api.get(`/company/users/${user.id}`);
      if (response.data.success) {
        setSelectedUser(response.data.data);
      }
    } catch (err) {
      console.error(err);
    }
    setShowDetailModal(true);
  };

  // Open permission modal
  const openPermissionModal = async (user) => {
    if (user.is_admin) {
      setError('Админ хэрэглэгчид бүх эрх автоматаар олгогдсон');
      return;
    }
    setSelectedUser(user);
    try {
      const response = await api.get(`/company/users/${user.id}`);
      if (response.data.success) {
        const userPermIds = response.data.data.permissions.map(p => p.id);
        setSelectedPermissions(userPermIds);
        setSelectedUser(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setSelectedPermissions([]);
    }
    setShowPermissionModal(true);
  };

  // Open delete modal
  const openDeleteModal = (user) => {
    if (user.is_primary) {
      setError('Үндсэн админыг устгах боломжгүй');
      return;
    }
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  // Open password modal
  const openPasswordModal = (user) => {
    if (user.is_primary && user.id !== currentUser?.id) {
      setError('Үндсэн админын нууц үгийг солих боломжгүй');
      return;
    }
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPasswordModal(true);
  };

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (modalMode === 'create') {
        if (formData.password.length < 6) {
          setError('Нууц үг 6-аас дээш тэмдэгттэй байх ёстой');
          setSaving(false);
          return;
        }
        const response = await api.post('/company/users', formData);
        if (response.data.success) {
          setSuccess('Хэрэглэгч амжилттай үүсгэлээ');
          fetchUsers();
          setShowModal(false);
        }
      } else {
        const { password, username, ...updateData } = formData;
        const response = await api.put(`/company/users/${selectedUser.id}`, updateData);
        if (response.data.success) {
          setSuccess('Хэрэглэгч амжилттай шинэчлэгдлээ');
          fetchUsers();
          setShowModal(false);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Handle permission submit
  const handlePermissionSubmit = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/company/users/${selectedUser.id}/permissions`, {
        permissions: selectedPermissions
      });
      if (response.data.success) {
        setSuccess('Эрхүүд амжилттай шинэчлэгдлээ');
        setShowPermissionModal(false);
        fetchUsers();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Эрх шинэчлэхэд алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setSaving(true);
    try {
      const response = await api.delete(`/company/users/${selectedUser.id}`);
      if (response.data.success) {
        setSuccess('Хэрэглэгч амжилттай устгалаа');
        fetchUsers();
        setShowDeleteModal(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Устгахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    if (newPassword.length < 6) {
      setError('Нууц үг 6-аас дээш тэмдэгттэй байх ёстой');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Нууц үг таарахгүй байна');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/company/users/${selectedUser.id}/password`, {
        password: newPassword
      });
      if (response.data.success) {
        setSuccess('Нууц үг амжилттай солигдлоо');
        setShowPasswordModal(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Нууц үг солиход алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // Toggle permission
  const togglePermission = (permId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permId)) {
        return prev.filter(id => id !== permId);
      } else {
        return [...prev, permId];
      }
    });
  };

  // Toggle all permissions in a module
  const toggleModulePermissions = (modulePerms) => {
    const modulePermIds = modulePerms.map(p => p.id);
    const allSelected = modulePermIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !modulePermIds.includes(id)));
    } else {
      setSelectedPermissions(prev => [...new Set([...prev, ...modulePermIds])]);
    }
  };

  // Select all permissions
  const selectAllPermissions = () => {
    const allIds = permissions.map(p => p.id);
    setSelectedPermissions(allIds);
  };

  // Clear all permissions
  const clearAllPermissions = () => {
    setSelectedPermissions([]);
  };

  // Get role info
  const getRoleInfo = (role) => {
    return ROLE_OPTIONS.find(r => r.value === role) || { label: role, color: 'bg-gray-100 text-gray-700', icon: '👤' };
  };

  // Module name translation
  const getModuleName = (module) => {
    const names = {
      warehouse: { name: 'Агуулах', icon: '🏭', color: 'border-green-200 bg-green-50' },
      shop: { name: 'Онлайн шоп', icon: '🛍️', color: 'border-purple-200 bg-purple-50' },
      product: { name: 'Бараа', icon: '📦', color: 'border-blue-200 bg-blue-50' },
      inventory: { name: 'Агуулахын нөөц', icon: '📊', color: 'border-indigo-200 bg-indigo-50' },
      delivery: { name: 'Хүргэлт', icon: '🚚', color: 'border-orange-200 bg-orange-50' },
      driver: { name: 'Жолооч', icon: '👨‍✈️', color: 'border-cyan-200 bg-cyan-50' },
      financial: { name: 'Санхүү', icon: '💰', color: 'border-yellow-200 bg-yellow-50' },
      settings: { name: 'Тохиргоо', icon: '⚙️', color: 'border-gray-200 bg-gray-50' },
      user: { name: 'Хэрэглэгч', icon: '👥', color: 'border-pink-200 bg-pink-50' }
    };
    return names[module] || { name: module, icon: '📁', color: 'border-gray-200 bg-gray-50' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <span className="text-3xl">👥</span>
              Хэрэглэгчид
            </h1>
            <p className="text-primary-100 mt-1">Компанийн хэрэглэгчдийн удирдлага</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Хайх..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2.5 bg-white/10 backdrop-blur border border-white/20 rounded-xl text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">🔍</span>
            </div>
            
            {hasPermission('user.create') && (
              <button
                onClick={openCreateModal}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-primary-600 rounded-xl hover:bg-primary-50 transition font-semibold shadow-lg"
              >
                <span className="text-lg">➕</span>
                Хэрэглэгч нэмэх
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-primary-100 text-sm">Нийт</p>
            <p className="text-2xl font-bold">{users.length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-primary-100 text-sm">Админ</p>
            <p className="text-2xl font-bold">{users.filter(u => u.role === 'company_admin').length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-primary-100 text-sm">Менежер</p>
            <p className="text-2xl font-bold">{users.filter(u => u.role === 'company_manager').length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-primary-100 text-sm">Ажилтан</p>
            <p className="text-2xl font-bold">{users.filter(u => u.role === 'company_worker').length}</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-3">
          <span className="text-xl">✅</span>
          <span className="font-medium">{success}</span>
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
          <span className="text-xl">❌</span>
          <span className="font-medium">{error}</span>
        </div>
      )}

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredUsers.map((user) => {
          const roleInfo = getRoleInfo(user.role);
          const isPrimary = user.is_primary;
          const isCurrentUser = user.id === currentUser?.id;
          
          return (
            <div 
              key={user.id} 
              className={`bg-white rounded-2xl border-2 hover:shadow-lg transition-all duration-300 overflow-hidden ${
                isPrimary ? 'border-yellow-300' : 'border-gray-200'
              }`}
            >
              {/* Card Header */}
              <div className={`p-4 ${isPrimary ? 'bg-gradient-to-r from-yellow-50 to-orange-50' : user.is_admin ? 'bg-red-50' : 'bg-gray-50'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold ${
                      isPrimary 
                        ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' 
                        : user.is_admin 
                          ? 'bg-red-100 text-red-600'
                          : 'bg-primary-100 text-primary-600'
                    }`}>
                      {roleInfo.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {user.full_name}
                        </h3>
                        {isPrimary && (
                          <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-bold rounded whitespace-nowrap">
                            ҮНДСЭН
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">@{user.username}</p>
                    </div>
                  </div>
                  
                  <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${
                    user.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {user.status === 'active' ? '● Идэвхтэй' : '○ Идэвхгүй'}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4 space-y-3">
                {/* Role */}
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${roleInfo.color}`}>
                    <span>{roleInfo.icon}</span>
                    {roleInfo.label}
                  </span>
                  {user.is_admin && !isPrimary && (
                    <span className="text-xs text-gray-500">(Бүх эрхтэй)</span>
                  )}
                </div>

                {/* Contact Info */}
                <div className="space-y-1.5 text-sm">
                  {user.email && (
                    <p className="flex items-center gap-2 text-gray-600">
                      <span className="text-gray-400">📧</span>
                      {user.email}
                    </p>
                  )}
                  {user.phone && (
                    <p className="flex items-center gap-2 text-gray-600">
                      <span className="text-gray-400">📱</span>
                      {user.phone}
                    </p>
                  )}
                </div>

                {/* Last Login */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    Сүүлд нэвтэрсэн: {user.last_login 
                      ? new Date(user.last_login).toLocaleString('mn-MN')
                      : 'Нэвтрээгүй'}
                  </p>
                </div>
              </div>

              {/* Card Actions */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => openDetailModal(user)}
                  className="text-sm text-gray-600 hover:text-primary-600 font-medium transition"
                >
                  Дэлгэрэнгүй →
                </button>
                
                <div className="flex items-center gap-1">
                  {/* Эрх тохируулах - зөвхөн admin биш хэрэглэгчид */}
                  {hasPermission('user.permissions') && !user.is_admin && (
                    <button
                      onClick={() => openPermissionModal(user)}
                      className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition"
                      title="Эрх тохируулах"
                    >
                      🔐
                    </button>
                  )}
                  
                  {/* Нууц үг солих - primary биш эсвэл өөрийгөө */}
                  {hasPermission('user.edit') && (!isPrimary || isCurrentUser) && (
                    <button
                      onClick={() => openPasswordModal(user)}
                      className="p-2 text-orange-600 hover:bg-orange-100 rounded-lg transition"
                      title="Нууц үг солих"
                    >
                      🔑
                    </button>
                  )}
                  
                  {/* Засах - primary биш */}
                  {hasPermission('user.edit') && !isPrimary && (
                    <button
                      onClick={() => openEditModal(user)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                      title="Засах"
                    >
                      ✏️
                    </button>
                  )}
                  
                  {/* Устгах - primary биш, өөрийгөө биш */}
                  {hasPermission('user.delete') && !isPrimary && !isCurrentUser && (
                    <button
                      onClick={() => openDeleteModal(user)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition"
                      title="Устгах"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredUsers.length === 0 && (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <span className="text-6xl">👥</span>
          <h3 className="text-xl font-semibold text-gray-900 mt-4">Хэрэглэгч олдсонгүй</h3>
          <p className="text-gray-500 mt-2">
            {searchTerm ? 'Хайлтын үр дүн олдсонгүй' : 'Хэрэглэгч нэмэхийн тулд дээрх товчийг дарна уу'}
          </p>
        </div>
      )}

      {/* ============================================
          CREATE/EDIT MODAL
          ============================================ */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-xl">{modalMode === 'create' ? '➕' : '✏️'}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {modalMode === 'create' ? 'Шинэ хэрэглэгч' : 'Хэрэглэгч засах'}
                    </h2>
                    <p className="text-primary-100 text-sm">Мэдээллийг бүрэн бөглөнө үү</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white transition"
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Role Selection - Card Style */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Үүрэг сонгох <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {ROLE_OPTIONS.map(role => (
                    <div
                      key={role.value}
                      onClick={() => setFormData(prev => ({ ...prev, role: role.value }))}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                        formData.role === role.value 
                          ? 'border-primary-500 bg-primary-50 shadow-md' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-2xl">{role.icon}</span>
                      <p className="font-semibold text-gray-900 mt-1">{role.label}</p>
                      <p className="text-xs text-gray-500">{role.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Username & Password Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Хэрэглэгчийн нэр <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">👤</span>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      disabled={modalMode === 'edit'}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition"
                      placeholder="username"
                      required
                    />
                  </div>
                </div>

                {modalMode === 'create' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Нууц үг <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔒</span>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                        placeholder="••••••••"
                        required
                        minLength={6}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Full name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Бүтэн нэр <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📝</span>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                    placeholder="Овог Нэр"
                    required
                  />
                </div>
              </div>

              {/* Email & Phone Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Имэйл
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📧</span>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Утас
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">📱</span>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                      placeholder="99001122"
                    />
                  </div>
                </div>
              </div>

              {/* Status (edit mode only) */}
              {modalMode === 'edit' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Төлөв
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition appearance-none bg-white"
                  >
                    {STATUS_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Info box */}
              {formData.role === 'company_admin' && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">👑</span>
                    <div>
                      <p className="font-semibold text-yellow-800">Админ эрх</p>
                      <p className="text-sm text-yellow-700">Энэ хэрэглэгч бүх эрхтэй байна, тусад нь эрх тохируулах шаардлагагүй.</p>
                    </div>
                  </div>
                </div>
              )}

              {formData.role !== 'company_admin' && modalMode === 'create' && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💡</span>
                    <div>
                      <p className="font-semibold text-blue-800">Эрх тохируулах</p>
                      <p className="text-sm text-blue-700">Хэрэглэгч үүсгэсний дараа 🔐 товч дарж эрхүүдийг тохируулна уу.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition font-medium"
                >
                  Болих
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Хадгалж байна...
                    </>
                  ) : (
                    <>
                      {modalMode === 'create' ? '➕ Үүсгэх' : '💾 Хадгалах'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================
          PERMISSION MODAL
          ============================================ */}
      {showPermissionModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-xl">🔐</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Эрх тохируулах</h2>
                    <p className="text-purple-100 text-sm">{selectedUser.full_name} ({selectedUser.username})</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPermissionModal(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white transition"
                >
                  ✕
                </button>
              </div>

              {/* Quick Actions */}
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={selectAllPermissions}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition"
                >
                  ✓ Бүгдийг сонгох
                </button>
                <button
                  onClick={clearAllPermissions}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-sm rounded-lg transition"
                >
                  ✕ Бүгдийг арилгах
                </button>
                <div className="flex-1"></div>
                <span className="px-3 py-1.5 bg-white/10 text-white text-sm rounded-lg">
                  {selectedPermissions.length} / {permissions.length} сонгосон
                </span>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {Object.keys(groupedPermissions).map(module => {
                const moduleInfo = getModuleName(module);
                const modulePerms = groupedPermissions[module];
                const selectedCount = modulePerms.filter(p => selectedPermissions.includes(p.id)).length;
                const allSelected = selectedCount === modulePerms.length;
                
                return (
                  <div key={module} className={`border-2 rounded-xl overflow-hidden ${moduleInfo.color}`}>
                    {/* Module header */}
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 transition"
                      onClick={() => toggleModulePermissions(modulePerms)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{moduleInfo.icon}</span>
                        <span className="font-semibold text-gray-900">{moduleInfo.name}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-medium px-2 py-1 rounded-lg ${
                          allSelected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {selectedCount} / {modulePerms.length}
                        </span>
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition ${
                          allSelected ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-300'
                        }`}>
                          {allSelected && '✓'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Permissions */}
                    <div className="p-4 pt-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {modulePerms.map(perm => (
                        <label 
                          key={perm.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                            selectedPermissions.includes(perm.id)
                              ? 'bg-purple-100 border-2 border-purple-300'
                              : 'bg-white border-2 border-transparent hover:border-gray-200'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPermissions.includes(perm.id)}
                            onChange={() => togglePermission(perm.id)}
                            className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-900">{perm.permission_name}</span>
                            <p className="text-xs text-gray-500">{perm.permission_key}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 rounded-b-2xl">
              <button
                onClick={() => setShowPermissionModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition font-medium"
              >
                Болих
              </button>
              <button
                onClick={handlePermissionSubmit}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Хадгалж байна...
                  </>
                ) : (
                  <>💾 Эрхүүд хадгалах</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          DETAIL MODAL
          ============================================ */}
      {showDetailModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8">
            {/* Modal Header */}
            <div className={`px-6 py-8 text-center rounded-t-2xl ${
              selectedUser.is_primary 
                ? 'bg-gradient-to-r from-yellow-400 to-orange-500' 
                : selectedUser.is_admin 
                  ? 'bg-gradient-to-r from-red-500 to-red-600'
                  : 'bg-gradient-to-r from-primary-600 to-primary-700'
            }`}>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold mx-auto mb-4 bg-white/20 text-white">
                {getRoleInfo(selectedUser.role).icon}
              </div>
              <h2 className="text-xl font-bold text-white">{selectedUser.full_name}</h2>
              <p className="text-white/80">@{selectedUser.username}</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                {selectedUser.is_primary && (
                  <span className="px-3 py-1 bg-white/20 text-white text-sm font-semibold rounded-full">
                    👑 Үндсэн админ
                  </span>
                )}
                {selectedUser.is_admin && !selectedUser.is_primary && (
                  <span className="px-3 py-1 bg-white/20 text-white text-sm font-semibold rounded-full">
                    Бүх эрхтэй
                  </span>
                )}
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Үүрэг</p>
                  <p className="font-medium text-gray-900">{getRoleInfo(selectedUser.role).label}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 mb-1">Төлөв</p>
                  <span className={`inline-flex px-2 py-0.5 text-sm font-medium rounded-full ${
                    selectedUser.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedUser.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}
                  </span>
                </div>
              </div>

              {(selectedUser.email || selectedUser.phone) && (
                <div className="p-4 bg-gray-50 rounded-xl space-y-2">
                  {selectedUser.email && (
                    <p className="flex items-center gap-2 text-gray-700">
                      <span>📧</span> {selectedUser.email}
                    </p>
                  )}
                  {selectedUser.phone && (
                    <p className="flex items-center gap-2 text-gray-700">
                      <span>📱</span> {selectedUser.phone}
                    </p>
                  )}
                </div>
              )}

              {/* Permissions */}
              {!selectedUser.is_admin && selectedUser.permissions && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span>🔐</span> Эрхүүд ({selectedUser.permissions.length})
                  </h3>
                  {selectedUser.permissions.length > 0 ? (
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                      {selectedUser.permissions.map(perm => (
                        <span 
                          key={perm.id}
                          className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-lg"
                        >
                          {perm.permission_name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">Эрх олгоогүй байна</p>
                  )}
                </div>
              )}

              {selectedUser.is_admin && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-yellow-800 text-sm">👑 Админ хэрэглэгч нь бүх эрхтэй</p>
                </div>
              )}

              <div className="pt-4 border-t border-gray-200 text-sm text-gray-500">
                <p>Сүүлд нэвтэрсэн: {selectedUser.last_login 
                  ? new Date(selectedUser.last_login).toLocaleString('mn-MN')
                  : 'Нэвтрээгүй'}</p>
                <p>Бүртгэгдсэн: {new Date(selectedUser.created_at).toLocaleString('mn-MN')}</p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition font-medium"
              >
                Хаах
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          DELETE MODAL
          ============================================ */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">⚠️</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Хэрэглэгч устгах</h2>
              <p className="text-gray-600 mb-6">
                <strong className="text-gray-900">{selectedUser.full_name}</strong> хэрэглэгчийг устгахдаа итгэлтэй байна уу?
                <br />
                <span className="text-sm text-red-500">Энэ үйлдлийг буцаах боломжгүй!</span>
              </p>
            </div>
            
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 rounded-b-2xl">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition font-medium"
              >
                Болих
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>🗑️ Устгах</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================
          PASSWORD MODAL
          ============================================ */}
      {showPasswordModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <span className="text-xl">🔑</span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Нууц үг солих</h2>
                  <p className="text-orange-100 text-sm">{selectedUser.full_name}</p>
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Шинэ нууц үг
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔒</span>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition"
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Хамгийн багадаа 6 тэмдэгт</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Нууц үг давтах
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔒</span>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent transition ${
                      confirmPassword && confirmPassword !== newPassword 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-gray-200'
                    }`}
                    placeholder="••••••••"
                  />
                </div>
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-500 mt-1">Нууц үг таарахгүй байна</p>
                )}
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 rounded-b-2xl">
              <button
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition font-medium"
              >
                Болих
              </button>
              <button
                onClick={handlePasswordReset}
                disabled={saving || newPassword.length < 6 || newPassword !== confirmPassword}
                className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>🔑 Нууц үг солих</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;